import React, { useEffect, useState } from "react";
import {
  Loader2, Plus, Edit3, Trash2, Power, PowerOff, MapPin, Building2, X, Check,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, resolveImage } from "../lib/api";
import { CITIES } from "../lib/data";
import useCityAreas from "../lib/useCityAreas";
import SearchableSelect from "../components/SearchableSelect";
import ImageUploader from "../components/ImageUploader";
import PdfUploader from "../components/PdfUploader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=400";

const PROJECT_TYPES = ["Residential", "Commercial", "Mixed", "Other"];
const PROJECT_STATUSES = ["Under Construction", "Ready to Move", "New Launch"];
const AREA_UNITS = ["Sq Ft", "Sq Yard", "Sq Meter", "Acre"];

const empty = () => ({
  title: "",
  description: "",
  projectType: "Residential",
  city: "",
  area: "",
  status: "Under Construction",
  totalArea: "",
  totalAreaUnit: "Sq Ft",
  priceMin: "",
  priceMax: "",
  images: [],
  pdfs: [],
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  active: true,
});

function ProjectForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(() => ({ ...empty(), ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const { areas } = useCityAreas(f.city);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const charCount = f.description.trim().length;

  const isEdit = !!initial?.id;

  const validate = () => {
    if (f.title.trim().length < 3) return "Title must be at least 3 characters.";
    if (charCount < 20) return `Description must have at least 20 characters (currently ${charCount}).`;
    if (!f.city) return "City is required.";
    if (!f.images.length) return "Upload at least one image.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        ...f,
        totalArea: f.totalArea === "" ? null : Number(f.totalArea),
        priceMin: f.priceMin === "" ? null : Number(f.priceMin),
        priceMax: f.priceMax === "" ? null : Number(f.priceMax),
      };
      const { data } = isEdit
        ? await api.put(`/admin/projects/${initial.id}`, payload)
        : await api.post("/admin/projects", payload);
      toast.success(isEdit ? "Project updated" : "Project added");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Title + Type */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <Label>Project Title *</Label>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Pataudi Greens Phase 2" className="mt-1.5" data-testid="proj-title" />
        </div>
        <div>
          <Label>Project Type</Label>
          <div className="mt-1.5">
            <SearchableSelect value={f.projectType} onChange={(v) => set("projectType", v)} options={PROJECT_TYPES} allowOther={false} testid="proj-type" />
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label>Description *</Label>
        <Textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the project — location advantages, amenities, USPs, target audience..." className="mt-1.5" data-testid="proj-description" />
        <div className="flex items-center justify-between mt-1 text-xs">
          <span className={charCount < 20 ? "text-destructive font-medium" : "text-emerald-600 font-medium"}>
            {charCount} / min 20 characters
          </span>
        </div>
      </div>

      {/* Images */}
      <div>
        <Label>Project Images * <span className="text-muted-foreground font-normal">(first image = thumbnail)</span></Label>
        <div className="mt-2">
          <ImageUploader images={f.images} onChange={(imgs) => set("images", imgs)} max={20} />
        </div>
      </div>

      {/* PDFs — optional brochures / floor plans */}
      <div>
        <Label>Project PDFs <span className="text-muted-foreground font-normal">(Optional — brochures, floor plans)</span></Label>
        <div className="mt-2">
          <PdfUploader value={f.pdfs || []} onChange={(pdfs) => set("pdfs", pdfs)} multiple maxMb={50} />
        </div>
      </div>

      {/* Location */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>City *</Label>
          <div className="mt-1.5">
            <SearchableSelect value={f.city} onChange={(v) => { set("city", v); set("area", ""); }} options={CITIES} allowOther={false} placeholder="Select city" testid="proj-city" />
          </div>
        </div>
        <div>
          <Label>Area</Label>
          <div className="mt-1.5">
            <SearchableSelect value={f.area} onChange={(v) => set("area", v)} options={areas} allowOther placeholder={f.city ? "Select area" : "Pick a city first"} disabled={!f.city} testid="proj-area" />
          </div>
        </div>
      </div>

      {/* Size + Status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <Label>Total Area <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <div className="mt-1.5 grid grid-cols-[1fr_140px] gap-2">
            <Input type="number" value={f.totalArea} onChange={(e) => set("totalArea", e.target.value)} placeholder="e.g. 5" data-testid="proj-total-area" />
            <SearchableSelect value={f.totalAreaUnit} onChange={(v) => set("totalAreaUnit", v)} options={AREA_UNITS} allowOther={false} testid="proj-total-area-unit" />
          </div>
        </div>
        <div>
          <Label>Status</Label>
          <div className="mt-1.5">
            <SearchableSelect value={f.status} onChange={(v) => set("status", v)} options={PROJECT_STATUSES} allowOther={false} testid="proj-status" />
          </div>
        </div>
      </div>

      {/* Price range */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Price From ₹ <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input type="number" value={f.priceMin} onChange={(e) => set("priceMin", e.target.value)} placeholder="e.g. 5000000" className="mt-1.5" data-testid="proj-price-min" />
        </div>
        <div>
          <Label>Price To ₹ <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input type="number" value={f.priceMax} onChange={(e) => set("priceMax", e.target.value)} placeholder="e.g. 12000000" className="mt-1.5" data-testid="proj-price-max" />
        </div>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-border bg-surface/40 p-4">
        <div className="sm:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact Details</p>
        </div>
        <div>
          <Label>Contact Name</Label>
          <Input value={f.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="e.g. Rohit Malik" className="mt-1.5" data-testid="proj-contact-name" />
        </div>
        <div>
          <Label>Phone Number</Label>
          <div className="relative mt-1.5">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground select-none">+91</span>
            <Input type="tel" inputMode="numeric" maxLength={10} value={f.contactPhone} onChange={(e) => set("contactPhone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit number" className="pl-12" data-testid="proj-contact-phone" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label>Contact Email</Label>
          <Input type="email" value={f.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} placeholder="e.g. sales@builder.com" className="mt-1.5" data-testid="proj-contact-email" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
        <Button variant="ghost" onClick={onCancel} data-testid="proj-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="proj-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> {isEdit ? "Save Changes" : "Add Project"}</>}
        </Button>
      </div>
    </div>
  );
}

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null);   // null | "new" | {id: ...}
  const [acting, setActing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/projects");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project permanently?")) return;
    setActing(id);
    try {
      await api.delete(`/admin/projects/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setActing(null); }
  };

  const toggle = async (id) => {
    setActing(id);
    try {
      const { data } = await api.post(`/admin/projects/${id}/toggle`);
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, active: data.active } : x));
      toast.success(data.active ? "Project activated" : "Project deactivated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed");
    } finally { setActing(null); }
  };

  return (
    <div data-testid="admin-projects-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display font-bold text-xl text-ink">Projects</h2>
          <p className="text-sm text-muted-foreground">Curated real-estate and business projects surfaced on the homepage carousel.</p>
        </div>
        <Button onClick={() => setOpenForm("new")} className="bg-primary hover:bg-primary/90 text-white gap-1.5" data-testid="admin-add-project">
          <Plus className="h-4 w-4" /> Add Project
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-md py-16 text-center text-muted-foreground" data-testid="admin-projects-empty">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground/60 mb-2" />
          No projects yet. Click <strong>Add Project</strong> to create the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => {
            const img = (p.images && p.images[0]) ? resolveImage(p.images[0]) : PLACEHOLDER;
            return (
              <article key={p.id} className={`bg-white rounded-md border overflow-hidden flex flex-col ${p.active ? "border-border" : "border-rose-200 opacity-70"}`} data-testid={`admin-project-${p.id}`}>
                <div className="aspect-[16/10] bg-slate-100 overflow-hidden relative">
                  <img src={img} alt={p.title || "Project"} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
                  <span className={`absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${p.active ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                  <span className="absolute top-2 right-2 text-[10px] font-medium bg-white/95 backdrop-blur px-2 py-0.5 rounded">
                    {p.status}
                  </span>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-ink line-clamp-2">{p.title}</h3>
                  <div className="text-sm text-ink/70 space-y-1 mt-1">
                    <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {[p.area, p.city].filter(Boolean).join(", ")}</p>
                    <p className="flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5 text-muted-foreground" /> {p.images?.length || 0} images · {p.projectType}</p>
                  </div>
                  <div className="mt-auto pt-4 grid grid-cols-3 gap-2">
                    <Button onClick={() => setOpenForm({ id: p.id, data: p })} variant="outline" className="rounded-sm h-9 gap-1 border-primary/40 text-primary hover:bg-primary/5" data-testid={`proj-edit-${p.id}`}>
                      <Edit3 className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button onClick={() => toggle(p.id)} disabled={acting === p.id} variant="outline" className={`rounded-sm h-9 gap-1 ${p.active ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`proj-toggle-${p.id}`}>
                      {acting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : p.active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                      {p.active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button onClick={() => remove(p.id)} disabled={acting === p.id} variant="ghost" className="rounded-sm h-9 gap-1 text-destructive hover:bg-destructive/10" data-testid={`proj-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!openForm} onOpenChange={(o) => { if (!o) setOpenForm(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="proj-form-dialog">
          <DialogHeader>
            <DialogTitle>{openForm && openForm !== "new" ? "Edit Project" : "Add Project"}</DialogTitle>
            <DialogDescription>Fill the details below. All admin-added projects auto-appear on the homepage carousel.</DialogDescription>
          </DialogHeader>
          {openForm && (
            <ProjectForm
              initial={openForm !== "new" ? openForm.data : null}
              onCancel={() => setOpenForm(null)}
              onSaved={(saved) => {
                setOpenForm(null);
                setItems((arr) => {
                  const exists = arr.find((x) => x.id === saved.id);
                  return exists ? arr.map((x) => x.id === saved.id ? saved : x) : [saved, ...arr];
                });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
